import { Link } from 'react-router-dom';
import { Truck, Shield, Star, CheckCircle2, ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';

const HERO_BG = 'https://d2xsxph8kpxj0f.cloudfront.net/310519663600218204/WUkEo5cvXZEkkjU2V2z9MY/gf-hero-bg-Vx3Jr2L4KerxExqcee3Knt.webp';
const TRUCK_IMG = 'https://d2xsxph8kpxj0f.cloudfront.net/310519663600218204/WUkEo5cvXZEkkjU2V2z9MY/gf-truck-hero-59TJJ7MLAMnWSBirHPZsds.webp';

export default function LandingPage() {
  return (
    <div className="min-h-screen bg-background text-foreground flex flex-col">

      {/* ── Nav ── */}
      <header className="absolute top-0 left-0 right-0 z-30 px-5 py-4 flex items-center justify-between">
        <span className="font-space font-bold text-sm tracking-widest text-foreground uppercase">GF Trucklines</span>
        <a href="#portals">
          <Button size="sm" className="bg-primary text-primary-foreground hover:bg-primary/90 text-xs px-3">
            Access Portal
          </Button>
        </a>
      </header>

      {/* ══════════════════════════════════════
          HERO — full-viewport image background
      ══════════════════════════════════════ */}
      <section className="relative w-full min-h-screen flex items-center justify-center overflow-hidden">
        {/* BG image */}
        <img
          src={HERO_BG}
          alt=""
          className="absolute inset-0 w-full h-full object-cover object-center"
        />
        {/* Gradient overlay */}
        <div className="absolute inset-0" style={{ background: 'linear-gradient(to bottom, hsl(var(--background) / 0.6) 0%, hsl(var(--background) / 0.75) 50%, hsl(var(--background)) 100%)' }} />

        {/* Hero content */}
        <div className="relative z-10 flex flex-col items-center text-center px-6 pt-20 pb-16 gap-5 w-full">
          {/* Giant headline */}
          <div className="font-space font-bold leading-none tracking-tight select-none">
            <div className="text-6xl sm:text-8xl lg:text-[10rem] text-foreground drop-shadow-lg">GF</div>
            <div className="text-5xl sm:text-8xl lg:text-[10rem] text-primary drop-shadow-lg break-words w-full">TRUCKLINES</div>
          </div>

          <p className="text-muted-foreground text-base sm:text-xl max-w-lg leading-relaxed">
            Premium Freight Logistics &amp; Owner-Operator Partnerships.
          </p>

          <a href="#portals">
            <Button size="lg" className="bg-primary text-primary-foreground hover:bg-primary/90 gap-2 text-base px-8">
              Access Your Portal <ArrowRight className="w-4 h-4" />
            </Button>
          </a>
        </div>
      </section>

      {/* ══════════════════════════════════════
          STATS BAR
      ══════════════════════════════════════ */}
      <section className="bg-card border-y border-border px-6 py-12">
        <div className="max-w-5xl mx-auto grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
          {[
            { value: '2,400+', label: 'Loads Delivered' },
            { value: '98.7%', label: 'On-Time Rate' },
            { value: '48 STATES', label: 'Coverage Area' },
            { value: '$1.2B+', label: 'Freight Moved' },
          ].map(({ value, label }) => (
            <div key={label} className="flex flex-col items-center gap-1">
              <span className="text-4xl md:text-5xl font-bold text-primary font-space">{value}</span>
              <span className="text-xs text-muted-foreground uppercase tracking-widest font-semibold">{label}</span>
            </div>
          ))}
        </div>
      </section>

      {/* ══════════════════════════════════════
          SPLIT PORTAL CARDS
      ══════════════════════════════════════ */}
      <section id="portals" className="px-6 py-20 max-w-5xl mx-auto w-full">
        <div className="text-center mb-12">
          <p className="text-xs font-semibold text-primary uppercase tracking-widest mb-2">Select Your Portal</p>
          <h2 className="font-space font-bold text-3xl sm:text-4xl text-foreground tracking-tight">Built for Every Partner</h2>
        </div>

        <div className="grid grid-cols-1 max-w-md mx-auto gap-6">

          {/* ── Card 1: Owner-Operators ── */}
          <div className="bg-card border border-border rounded-xl overflow-hidden flex flex-col">
            {/* Image half */}
            <div className="relative h-52 overflow-hidden flex-shrink-0">
              <img src={TRUCK_IMG} alt="Owner-Operators" className="w-full h-full object-cover" />
              <div className="absolute inset-0" style={{ background: 'linear-gradient(to bottom, transparent 30%, hsl(var(--card)) 100%)' }} />
              {/* Floating icon */}
              <div className="absolute top-4 left-4 w-10 h-10 rounded-xl bg-card/80 backdrop-blur-sm border border-border flex items-center justify-center">
                <Truck className="w-5 h-5 text-primary" />
              </div>
            </div>

            {/* Body */}
            <div className="p-7 flex flex-col gap-5 flex-1">
              <div>
                <p className="text-xs font-semibold text-primary uppercase tracking-widest mb-1">For Owner-Operators</p>
                <h3 className="font-space font-bold text-xl text-foreground">Driver Portal</h3>
                <p className="text-muted-foreground text-sm mt-1.5 leading-relaxed">
                  Access your financial dashboard, view active loads, and manage settlements.
                </p>
              </div>

              <ul className="space-y-2.5">
                {['Transparent per-load settlements', 'Fuel advance program available', 'Weekly direct deposit payouts', 'Dedicated dispatch support'].map(b => (
                  <li key={b} className="flex items-center gap-2.5 text-sm text-muted-foreground">
                    <CheckCircle2 className="w-4 h-4 text-primary flex-shrink-0" />
                    {b}
                  </li>
                ))}
              </ul>

              <div className="flex flex-col gap-2.5 mt-auto pt-2">
                <Link to="/login"><Button className="w-full bg-primary text-primary-foreground hover:bg-primary/90">Driver Sign In</Button></Link>
                <Link to="/register"><Button variant="outline" className="w-full border-primary text-primary hover:bg-primary/10">Apply to Haul</Button></Link>
              </div>
            </div>
          </div>

        </div>
      </section>

      {/* ══════════════════════════════════════
          ASYMMETRIC TRUST FOOTER
      ══════════════════════════════════════ */}
      <section className="border-t border-border px-6 py-20">
        <div className="max-w-5xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-14 items-start">

          {/* Left — Statement */}
          <div className="flex flex-col gap-6">
            <p className="text-xs font-semibold text-primary uppercase tracking-widest">Why GF Trucklines</p>
            <h2 className="font-space font-bold text-4xl sm:text-5xl leading-tight tracking-tight text-foreground">
              BUILT ON <span className="text-primary">TRUST &amp;</span> PERFORMANCE
            </h2>
            <p className="text-muted-foreground leading-relaxed max-w-md">
              Since 2008, GF Trucklines has operated on a simple principle: take care of the operators who carry your freight, and quality will follow. We partner with owner-operators who share our commitment to reliability and professionalism.
            </p>
          </div>

          {/* Right — Feature cards stacked */}
          <div className="flex flex-col gap-4">
            {[
              { Icon: Shield, title: 'Fully Insured & Compliant', desc: 'Operating with full DOT/FMCSA compliance and comprehensive cargo coverage on every load.' },
              { Icon: Star, title: 'Operator-First Culture', desc: 'Built by truckers, for truckers. Your success is the foundation of our entire network.' },
              { Icon: CheckCircle2, title: 'Transparent Settlements', desc: 'Every deduction itemized. Every dollar accounted for. No surprises on payday.' },
            ].map(({ Icon, title, desc }) => (
              <div key={title} className="p-5 rounded-xl border border-border bg-card/50 flex items-start gap-4">
                <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                  <Icon className="w-5 h-5 text-primary" />
                </div>
                <div>
                  <p className="font-space font-semibold text-foreground mb-1">{title}</p>
                  <p className="text-xs text-muted-foreground leading-relaxed">{desc}</p>
                </div>
              </div>
            ))}
          </div>

        </div>
      </section>

      {/* ── Footer ── */}
      <footer className="border-t border-border px-6 py-6 text-center text-xs text-muted-foreground">
        © {new Date().getFullYear()} GF Trucklines. All rights reserved.
      </footer>
    </div>
  );
}